import React from 'react';
import Immutable from 'immutable';

import type { NotificationSettings } from 'js/src/client';
import { ApiService } from 'js/src/client';
import SettingsToggle from 'js/src/components/common/SettingsToggle';
import { Toast } from 'js/src/components/common/Toast';
import { SecondaryButton } from 'js/src/components/common/ui/Button';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';

const analyticsSubscriptionEventName = 'Email Subscriptions Changed';

type Props = {
    user_id: number;
    email_secret_uuid: string;
    unsub_on_load?: string;
};

const NotificationManagement = React.memo(function NotificationManagement({
    user_id,
    email_secret_uuid,
    unsub_on_load,
}: Props) {
    const [notificationSettings, setNotificationSettings] = React.useState<
        ImmutableMap<NotificationSettings>
    >(Immutable.Map() as unknown as ImmutableMap<NotificationSettings>);
    const [loading, setLoading] = React.useState(true);

    const saveSettings = React.useCallback(
        (
            notificationSettings: ImmutableMap<NotificationSettings>,
            suppressNotification = false,
        ) => {
            ApiService.userNotificationsPartialUpdate({
                path: {
                    email_secret_uuid: email_secret_uuid,
                    user_id: user_id.toString(),
                },
                body: notificationSettings.toJS(),
            })
                .then((response) => {
                    if (!suppressNotification) {
                        Toast.success('Your settings have been updated. Thanks!');
                    }
                    setLoading(false);
                    setNotificationSettings(ImmutableFromJS(response.data));
                })
                .catch((e: unknown) => {
                    ErrorReporter.capture(e);
                    alert(
                        'There was an issue updating your preferences. Please refresh the page and try again.',
                    );
                });
        },
        [email_secret_uuid, user_id],
    );

    const renderSubscriptionChange = React.useCallback(
        (newValue: boolean, suppressNotification: boolean) => {
            analytics.track(analyticsSubscriptionEventName, {
                category: 'render',
                value: newValue,
            });
            const newNotificationSettings = notificationSettings.set(
                'render_emails_unsubscribed',
                newValue,
            );
            setNotificationSettings(newNotificationSettings);
            saveSettings(newNotificationSettings, suppressNotification);
        },
        [notificationSettings, saveSettings],
    );

    const processUnsubOnLoad = React.useCallback(() => {
        if (unsub_on_load) {
            switch (unsub_on_load) {
                case 'render':
                    renderSubscriptionChange(true, true);
                    break;
                case 'support': // Everybody should be subscribed to support email in the future, because of that, user should not be able to unsubscribe
                case 'notification': // Everybody should be subscribed to notification email in the future, because of that, user should not be able to unsubscribe
                default:
                    alert('invalid unsubscribe request: please contact support');
                    break;
            }
        }
    }, [renderSubscriptionChange, unsub_on_load]);

    const loadNotificationSettings = React.useCallback(() => {
        ApiService.userNotificationsRetrieve({
            path: {
                email_secret_uuid: email_secret_uuid,
                user_id: user_id.toString(),
            },
        })
            .then((response) => {
                setLoading(false);
                setNotificationSettings(ImmutableFromJS(response.data));
                processUnsubOnLoad();
            })
            .catch((e: unknown) => {
                ErrorReporter.capture(e);
                Toast.error(
                    'Error loading notification data from server. Please refresh the page and try again.',
                );
            });
    }, [email_secret_uuid, processUnsubOnLoad, user_id]);

    React.useEffect(() => {
        // Only load once. If the notificationSettings is populated, it means
        // we already loaded before so we should just skip.
        if (notificationSettings.count() !== 0) return;
        loadNotificationSettings();
    }, [loadNotificationSettings, notificationSettings]);

    const unsubscribeAll = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            analytics.track(analyticsSubscriptionEventName, {
                category: 'all',
                value: 'true',
            });
            analytics.trackHSEvent(analyticsSubscriptionEventName, {
                hs_email_optout_10619513: true,
                opted_in_promotional_emails: false,
                hs_email_optout_10619656: true,
            });
            const newNotificationSettings = notificationSettings
                .set('render_emails_unsubscribed', true)
                .set('has_opt_in_newsletter_emails', false);
            setNotificationSettings(newNotificationSettings);
            saveSettings(newNotificationSettings, false);
        },
        [notificationSettings, saveSettings],
    );

    if (loading) {
        return null;
    }

    return (
        <div>
            <div className="card mb-l5-48">
                <div className="p-l5-24">
                    <p className="mb-l5-8 fw-bold">EMAIL NOTIFICATIONS</p>
                    <hr />
                    <SettingsToggle
                        checked={!notificationSettings.get('render_emails_unsubscribed')}
                        onChange={(newVal) => renderSubscriptionChange(!newVal, false)}
                        title="Render notifications"
                        description="When your video is finished rendering, we'll send you a notification email letting you know your video is ready."
                    />
                </div>
            </div>
            <div className="card mb-l5-48">
                <div className="p-l5-24">
                    <p className="fw-bold">UNSUBSCRIBE FROM ALL EMAILS</p>
                    <hr />
                    <div className="row">
                        <div className="col-sm-11">
                            <div className="row">
                                <div className="col-lg-4">
                                    <label>Don&apos;t want any emails?</label>
                                </div>
                                <p className="col-lg-8">
                                    If you don&apos;t want to receive any emails from us, you can
                                    unsubscribe from all emails. We won&apos;t send you any emails
                                    except for important notifications as required by law.
                                </p>
                            </div>
                        </div>
                    </div>
                    <br />
                    <div className="text-end">
                        <SecondaryButton
                            onClick={unsubscribeAll}
                            text="Unsubscribe from all emails"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default NotificationManagement;
